<template>
  <div v-if="!isLoading">
    <b-container>
      <b-row class="mb-4">
        <b-col>
          <h2>{{ meeting.name }}</h2>
          <h5 v-if="minuteEventMeetingStart">
            <span>Date :</span>
            {{ $moment(toUtc(minuteEventMeetingStart.date)).format("DD/MM/YYYY") }}
          </h5>
        </b-col>
      </b-row>
      <hr />
      <b-row class="mb-3">
        <b-col cols="12" lg="8">
          <h3>Les documents communs pour la convocation</h3>
        </b-col>
        <b-col cols="12" lg="4" class="d-flex justify-content-end align-items-center flex-wrap">
          <b-badge
            v-for="(invoice, index) in invoices"
            :key="index"
            variant="danger"
            class="mr-1 mb-1"
            pill
          >
            <b-link
              class="text-white text-decoration-none"
              target="_blank"
              :href="invoice.file_path"
            >
              <font-awesome-icon icon="file-pdf" size="1x" class="mr-1" />
              <span v-if="invoice.file_type == 'agency-invitation'">F. Convocation Ag.</span>
              <span v-if="invoice.file_type == 'building-invitation'">F. Convocation Immeuble</span>
              <span v-if="invoice.file_type == 'agency-pv'">F. PV Ag</span>
              <span v-if="invoice.file_type == 'building-pv'">F. Pv Immeuble</span>
            </b-link>
          </b-badge>
        </b-col>
      </b-row>
      <b-row class="mb-4">
        <b-col>
          <b-badge v-if="zipPdf" variant="info" class="mr-1" pill>
            <b-link class="text-white text-decoration-none" target="_blank" :href="zipPdf">
              <font-awesome-icon icon="file-pdf" size="1x" />
              ZIP
            </b-link>
          </b-badge>
          <b-badge v-if="agendaPdf" variant="success" class="mr-1" pill>
            <b-link class="text-white text-decoration-none" target="_blank" :href="agendaPdf">
              <font-awesome-icon icon="file-pdf" size="1x" />
              Ordre du jour
            </b-link>
          </b-badge>
          <b-badge v-if="minutesPdf" variant="dark" class="mr-1" pill>
            <b-link class="text-white text-decoration-none" target="_blank" :href="minutesPdf">
              <font-awesome-icon icon="file-pdf" size="1x" />
              Procès verbal
            </b-link>
          </b-badge>
          <b-badge v-if="presencePdf" variant="dark" class="mr-1" pill>
            <b-link class="text-white text-decoration-none" target="_blank" :href="presencePdf">
              <font-awesome-icon icon="file-pdf" size="1x" />
              Présence
            </b-link>
          </b-badge>
          <b-badge v-if="archivedDocPdf" variant="dark" class="mr-1" pill>
            <b-link class="text-white text-decoration-none" target="_blank" :href="archivedDocPdf">
              <font-awesome-icon icon="file-pdf" size="1x" />
              Archivé
            </b-link>
          </b-badge>
        </b-col>
        <b-col
          v-if="
            !pvReadyOrSent && meetingIsCompleted && !meeting.is_scheduled_to_generate_pv_antet_pdfs
          "
          cols="auto"
        >
          <b-button
            :disabled="!!signedPresenceMedia"
            variant="danger"
            @click="selectSignedPresence"
          >
            <font-awesome-icon icon="file-upload" size="1x" />
            Ajouter la feuille de présence signée
          </b-button>
        </b-col>
        <b-col
          v-if="
            !pvReadyOrSent && meetingIsCompleted && !meeting.is_scheduled_to_generate_pv_antet_pdfs
          "
          cols="auto"
        >
          <b-button :disabled="!!signedPvMedia" variant="danger" @click="selectSignedPv">
            <font-awesome-icon icon="file-upload" size="1x" />
            Ajouter le PV signé
          </b-button>
          <br />
          <span class="text-danger">* nécessaire avant d'envoyer</span>
        </b-col>
      </b-row>
      <b-row v-if="!minutesPdf && meetingIsCompleted">
        <b-col>
          <div class="d-flex align-items-center text-danger mt-2 mb-4">
            <font-awesome-icon icon="info-circle" class="mr-2" />
            <div>
              Vous devez générer le PV et la Présence avant d'envoyer le fichier au propriétaire
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row
        v-if="minutesPdf && meetingIsCompleted && !meeting.is_scheduled_to_generate_pv_antet_pdfs"
        class="mb-5"
      >
        <b-col>
          <b-row v-if="!pvReadyOrSent" class="mb-4">
            <b-col>
              <h3>Documents à envoyer à l'imprimeur</h3>
              <div @mousedown="selectAdditionalPrintMedia">
                <template>
                  <VueFileAgent
                    ref="vueFileAgent"
                    v-model="fileRecords"
                    :theme="'list'"
                    :multiple="true"
                    :deletable="true"
                    :linkable="true"
                    :editable="true"
                    :sortable="isSortable"
                    :accept="'application/pdf'"
                    :help-text="'Ajouter un document'"
                    :error-text="{
                      type: 'Type de fichier invalide. Seuls les fichiers PDF sont autorisés',
                    }"
                    :disabled="pvReadyOrSent"
                    @select="filesSelected($event)"
                    @beforedelete="onBeforeDelete($event)"
                    @delete="fileDeleted($event)"
                    @rename="fileChanged($event)"
                  ></VueFileAgent>
                </template>
              </div>
              <b-form-checkbox
                v-if="!pvReadyOrSent"
                v-model="isSortable"
                class="mt-1"
                :disabled="!fileRecords.length"
                :value="true"
                :unchecked-value="false"
              >
                <div class="ml-2">
                  <span v-if="!isSortable">Activer ordre</span>
                  <span v-else>Désactiver ordre</span>
                </div>
              </b-form-checkbox>
            </b-col>
          </b-row>
          <b-row v-if="minutesPdf && signedPvMedia">
            <b-col>
              <b-row>
                <b-col class="text-center">
                  <b-button
                    variant="outline-secondary"
                    @click="GENERATE_PDF_PRIMARY_FUNCTION(CREATE_MERGE_PV_PREVIEW)"
                  >
                    <font-awesome-icon icon="file-pdf" size="1x" />
                    <span class="mx-1">
                      Prévisualiser le procès verbal fusionné
                    </span>
                  </b-button>
                </b-col>
              </b-row>
              <b-row class="my-4">
                <b-col class="text-center">
                  <b-button
                    v-if="!pvReadyOrSent"
                    variant="danger"
                    @click="createPvAntetAndUpdatePvReadyToSend"
                  >
                    <font-awesome-icon icon="file-upload" />
                    <span class="mx-1">
                      Envoyer le PV et les documents supplémentaires à l'impression
                    </span>
                  </b-button>
                  <h3 v-if="pvReady" class="text-info">
                    PV et documents supplémentaires sont prêts à être envoyés à
                    l'impression&nbsp;...
                  </h3>
                  <h3 v-if="pvSent" class="text-success">
                    PV et documents supplémentaires envoyé à l'impression
                    <font-awesome-icon icon="check" />
                  </h3>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row
        v-if="!!meeting.is_scheduled_to_generate_pv_antet_pdfs"
        class="generate-pv-antet-pdfs-progress my-5"
      >
        <b-col>
          <appGeneratePDFsProgress :generate-progress="meeting.generate_pv_antet_pdfs_progress" />
        </b-col>
      </b-row>
      <b-row>
        <b-col
          v-if="!!meeting.is_scheduled_to_generate_pdfs"
          class="generate-pv-antet-pdfs-progress my-3"
        >
          <appGeneratePDFsProgress :generate-progress="meeting.generate_pdfs_progress" />
        </b-col>
        <b-col v-else-if="meeting.status == meetingStatuses.mandatesCreated" cols="12" class="mb-4">
          <appDropzoneInvitationDocuments :meeting="meeting"></appDropzoneInvitationDocuments>
          <div class="d-flex justify-content-center">
            <b-button
              variant="outline-primary"
              class="mr-3"
              @click="GENERATE_PDF_PRIMARY_FUNCTION(GENERATE_PDF_FOR_ONE_MANDATE)"
              >Prévisualiser</b-button
            >
            <b-button variant="danger" @click="GENERATE_PDF_PRIMARY_FUNCTION(GENERATE_PDFS)"
              >Générer les invitations</b-button
            >
          </div>
        </b-col>
      </b-row>
      <b-row
        v-if="
          !meeting.is_scheduled_to_generate_pdfs &&
          meeting.status == meetingStatuses.mandatesCreated &&
          meeting.agenda_media_id != undefined
        "
      >
        <b-col class="d-flex justify-content-center" offset="4">
          <b-button
            v-if="mandates"
            variant="danger"
            :disabled="
              isDefaultInvitationBroadcastNotSet || !!meeting.is_scheduled_to_generate_pdfs
            "
            @click="showAskNotificationTypeModal"
          >
            <font-awesome-icon icon="file-alt" size="1x" />
            <span class="mx-1">
              Envoyez les convocations
            </span>
          </b-button>
          <b-button
            v-if="isDefaultInvitationBroadcastNotSet"
            variant="danger"
            class="ml-2"
            @click="getDefaultAgencyPreferredInvitationBroadcast"
          >
            <font-awesome-icon icon="file-alt" size="1x" />
            <span class="mx-1">
              Définir la diffusion d'invitation par défaut
            </span>
          </b-button>
        </b-col>
        <b-col cols="4">
          <label for="select-docs-print-color">Type d'impression</label>
          <v-select
            v-model="docsPrintColor"
            class="select-docs-print-color"
            :options="docsPrintColorOptions"
            :clearable="false"
            @input="updateMeetingDocPrintColor"
          >
          </v-select>
        </b-col>
      </b-row>

      <b-row class="justify-content-center">
        <b-col cols="12">
          <b-row class="justify-content-between mb-2">
            <b-col
              v-if="!showRepresentedRecipientsTable"
              cols="3"
              sm="6"
              md="3"
              class="d-flex align-items-end"
            >
              <div>
                <label for="input">Rechercher</label>
                <b-input-group>
                  <template #append>
                    <b-input-group-text
                      v-if="!hasSearchInputBeenReseted || isSearchInputFocused || searchParams"
                      type="button"
                      @click="resetSearch"
                    >
                      <font-awesome-icon icon="times" size="sm"></font-awesome-icon>
                    </b-input-group-text>
                  </template>

                  <b-form-input
                    id="input"
                    v-model="searchParams"
                    placeholder="Tapez pour rechercher"
                    @focus="focusSearch"
                    @blur="unfocusSearch"
                    @keyup.enter="search"
                  />
                </b-input-group>
                <small class="text-secondary ml-2"
                  >Utilisez la touche Entrée pour lancer la recherche</small
                >
              </div>
            </b-col>
            <b-col v-if="sendInvitationOrPvToAslCoOwners" class="d-flex align-items-center">
              <div
                class="btn btn-outline-primary"
                @click="showRepresentedRecipientsTable = !showRepresentedRecipientsTable"
              >
                <span v-if="!showRepresentedRecipientsTable"
                  >Afficher les copropriétaires représentés</span
                >
                <span v-else>Afficher les volumes</span>
              </div>
            </b-col>
          </b-row>
        </b-col>

        <b-col v-if="buildingOwnersItems.length > 0 && !showRepresentedRecipientsTable">
          <hr />
          <h3 class="mb-3">Documents individuels pour la convocation</h3>
          <b-table
            id="meeting-documents"
            hover
            sort-icon-left
            :fields="buildingOwnersFields"
            :items="buildingOwnersItems"
            :current-page="currentPageBuildingOwners"
            :per-page="perPageBuildingOwners"
            :busy="isTableLoading"
            :sort-by="sortBy"
            :sort-desc="sortDesc"
            responsive="sm"
            :tbody-tr-class="detailsClassFn"
          >
            <!-- Heads -->
            <template #head(name)>
              <div class="d-flex align-items-center">
                <span class="mr-2 cursor-pointer" @click="setBoSortByName()">Nom</span>
                <font-awesome-icon
                  v-if="boSortByName == 'DESC'"
                  icon="sort-down"
                  @click="setBoSortByName()"
                ></font-awesome-icon>
                <font-awesome-icon
                  v-else-if="boSortByName == 'ASC'"
                  icon="sort-up"
                  @click="setBoSortByName()"
                ></font-awesome-icon>
              </div>
            </template>
            <template #head(gender)="field">
              <div v-show="!isMeetingAsl">
                {{ field.label }}
              </div>
            </template>
            <template #head(volume)="field">
              <div v-show="isMeetingAsl">
                {{ field.label }}
              </div>
            </template>
            <template #head(cogeprint)="field">
              <div v-show="meeting.status != meetingStatuses.mandatesCreated">
                {{ field.label }}
              </div>
            </template>
            <template #head(agency_preferred_broadcast)="field">
              <div v-show="showPreferredBroadcast">
                {{ field.label }}
              </div>
            </template>
            <template #head(agency_preferred_invitation_broadcast)="field">
              <div v-show="showAgencyPreferredInvitationBroadcastField">
                {{ field.label }}
              </div>
            </template>
            <!-- Cells -->
            <template #cell(index)="data">
              {{ pageIncrementalIndex + data.index + 1 }}
            </template>

            <template #cell(gender)="data">
              <div v-show="!isMeetingAsl">{{ data.item.gender }}</div>
            </template>

            <template #cell(name)="data">
              <span v-if="data.item.buildingOwnerName">
                {{ data.item.buildingOwnerName + " - " }}
              </span>
              <span>{{ data.item.name }}</span>
            </template>

            <template #cell(volume)="data">
              <span v-if="isMeetingAsl">{{ getBuildingAslName(data.item.building_asl_id) }}</span>
            </template>

            <template #cell(documents)="data">
              <b-badge
                v-if="data.item.documents && data.item.documents.invitationPdf"
                variant="success"
                class="mr-1"
                pill
              >
                <b-link
                  class="small text-white text-decoration-none"
                  target="_blank"
                  :href="data.item.documents.invitationPdf"
                >
                  <font-awesome-icon icon="file-pdf" size="1x" />
                  Convocation
                </b-link>
              </b-badge>
              <b-badge
                v-if="data.item.documents && data.item.documents.pouvoirPdf"
                variant="success"
                class="mr-1"
                pill
              >
                <b-link
                  class="small text-white text-decoration-none"
                  target="_blank"
                  :href="data.item.documents.pouvoirPdf"
                >
                  <font-awesome-icon icon="file-pdf" size="1x" />
                  Pouvoir
                </b-link>
              </b-badge>
              <b-badge
                v-if="data.item.documents && data.item.documents.vbcPdf"
                variant="success"
                class="mr-1"
                pill
              >
                <b-link
                  class="small text-white text-decoration-none"
                  target="_blank"
                  :href="data.item.documents.vbcPdf"
                >
                  <font-awesome-icon icon="file-pdf" size="1x" />
                  Correspondance
                </b-link>
              </b-badge>
              <b-badge
                v-if="data.item.documents && data.item.documents.mergedPdf"
                variant="info"
                class="mr-1"
                pill
              >
                <b-link
                  class="small text-white text-decoration-none"
                  target="_blank"
                  :href="data.item.documents.mergedPdf"
                >
                  <font-awesome-icon icon="file-pdf" size="1x" />
                  L'invitation à la convocation, fusionné
                </b-link>
              </b-badge>
              <b-badge
                v-if="data.item.documents && data.item.documents.mergedPvMedia"
                variant="dark"
                class="mr-1"
                pill
              >
                <b-link
                  class="small text-white text-decoration-none"
                  target="_blank"
                  :href="data.item.documents.mergedPvMedia"
                >
                  <font-awesome-icon icon="file-pdf" size="1x" />
                  Le procès-verbal, fusionné
                </b-link>
              </b-badge>

              <div
                v-if="data.item.documents && data.item.documents.individualAdditionalFileInvitation"
              >
                <div
                  v-if="
                    (addInvitationIndividualFiles ||
                      data.item.documents.individualAdditionalFileInvitation.length > 0) &&
                    (data.item.documents.invitationPdf ||
                      data.item.documents.pouvoirPdf ||
                      data.item.documents.vbcPdf)
                  "
                  class="d-flex align-items-center mt-1"
                >
                  <span class="mr-2 small text-nowrap"
                    >Documents individuels pour l'invitation</span
                  >
                  <hr class="d-inline-block w-100" />
                </div>

                <b-badge
                  v-for="(individualInvitationFile, index) in data.item.documents
                    .individualAdditionalFileInvitation"
                  :key="index"
                  variant="light"
                  class="mr-1"
                  pill
                >
                  <b-link
                    class="small text-dark text-decoration-none"
                    target="_blank"
                    :href="individualInvitationFile.path"
                  >
                    <font-awesome-icon icon="file-pdf" size="1x" />
                    {{ individualInvitationFile.label }}
                  </b-link>
                  <div
                    v-if="addInvitationIndividualFiles && !meeting.is_scheduled_to_generate_pdfs"
                    class="btn text-danger p-0 pl-1"
                    @click="
                      deleteIndividualFile(
                        individualInvitationFile,
                        index,
                        data.item.mandateId,
                        'invitation'
                      )
                    "
                  >
                    <font-awesome-icon icon="trash" size="sm" />
                  </div>
                </b-badge>
                <span v-if="addInvitationIndividualFiles && !meeting.is_scheduled_to_generate_pdfs">
                  <div
                    class="btn btn-secondary ml-2 p-1"
                    @click="uploadIndividualFiles(data.item.mandateId, 'invitation')"
                  >
                    <span class="small mr-2">Ajouter</span>
                    <font-awesome-icon icon="file-upload" size="sm" />
                  </div>
                  <input
                    id="formFile"
                    :ref="'individualInvitationFile-' + data.item.mandateId"
                    class="invisible"
                    type="file"
                    multiple
                    @change="uploadedIndividualFiles($event, data.item.mandateId, 'invitation')"
                  />
                </span>
              </div>

              <div v-if="data.item.documents && data.item.documents.individualAdditionalFilesPv">
                <div
                  v-if="
                    (addPvIndividualFiles ||
                      data.item.documents.individualAdditionalFilesPv.length > 0) &&
                    (data.item.documents.invitationPdf ||
                      data.item.documents.pouvoirPdf ||
                      data.item.documents.vbcPdf)
                  "
                  class="d-flex align-items-center mt-1"
                >
                  <div class="small mr-2 text-nowrap">Documents individuels pour procès verbal</div>
                  <hr class="d-inline-block w-100" />
                </div>

                <b-badge
                  v-for="(individualInvitationFile, index) in data.item.documents
                    .individualAdditionalFilesPv"
                  :key="index"
                  variant="light"
                  class="mr-1"
                  pill
                >
                  <b-link
                    class="small text-dark text-decoration-none"
                    target="_blank"
                    :href="individualInvitationFile.path"
                  >
                    <font-awesome-icon icon="file-pdf" size="1x" />
                    {{ individualInvitationFile.label }}
                  </b-link>
                  <div
                    v-if="addPvIndividualFiles && !meeting.is_scheduled_to_generate_pv_antet_pdfs"
                    class="btn text-danger p-0 pl-1"
                    @click="
                      deleteIndividualFile(
                        individualInvitationFile,
                        index,
                        data.item.mandateId,
                        'pv'
                      )
                    "
                  >
                    <font-awesome-icon icon="trash" size="sm" />
                  </div>
                </b-badge>
                <span
                  v-if="addPvIndividualFiles && !meeting.is_scheduled_to_generate_pv_antet_pdfs"
                >
                  <div
                    class="btn btn-secondary ml-2 p-1"
                    @click="uploadIndividualFiles(data.item.mandateId, 'pv')"
                  >
                    <span class="small mr-2">Ajouter</span>
                    <font-awesome-icon icon="file-upload" size="1x" />
                  </div>
                  <input
                    id="formFile"
                    :ref="'individualPvFile-' + data.item.mandateId"
                    class="invisible"
                    type="file"
                    multiple
                    @change="uploadedIndividualFiles($event, data.item.mandateId, 'pv')"
                  />
                </span>
              </div>
            </template>

            <template #cell(agency_preferred_broadcast)="data">
              <v-select
                v-show="showPreferredBroadcast"
                v-model="data.item.agency_preferred_broadcast"
                class="select-list-width-auto"
                :options="agencyPreferredPvBroadcastOptions(data.item)"
                :clearable="false"
                :disabled="isPreferredBroadcastDisabled"
                @input="updatePrefferedBroadcast($event, data.item)"
              >
                <span slot="no-options">
                  {{ $t("general.select_with_input_no_option") }}
                </span>
              </v-select>
            </template>

            <template #cell(agency_preferred_invitation_broadcast)="data">
              <v-select
                v-show="showInvitationBroadcastField(data.item)"
                v-model="data.item.agency_preferred_invitation_broadcast"
                class="select-list-width-auto"
                :options="agencyPreferredInvitationBroadcastOptions(data.item)"
                :clearable="false"
                :disabled="isSelectedPreferredInvitationDisabled"
                @input="updatePrefferedInvitationBroadcast($event, data.item)"
              >
                <span slot="no-options">
                  {{ $t("general.select_with_input_no_option") }}
                </span>
              </v-select>
            </template>

            <template #cell(cogeprint)="data">
              <div v-show="pvReadyOrSent || meeting.status != meetingStatuses.mandatesCreated">
                <font-awesome-icon
                  class="cursor-pointer"
                  icon="info-circle"
                  size="1x"
                  @click="toggleDetails(data)"
                />
                <span v-if="data.item.docsStatus && data.item.docsStatus.length > 0" class="ml-2">
                  {{ getLastDocStatusDetails(data.item.docsStatus) }}
                </span>
                <span
                  v-if="data.item.docsStatus && data.item.docsStatus.length > 0"
                  class="font-10"
                >
                  {{ " (" + getLastDocStatusDateTime(data.item.docsStatus) + ")" }}
                </span>
              </div>
            </template>

            <template #cell(infoPV)="data">
              <b-badge v-if="data.item.infoPV.minutesPdf" variant="success" class="mr-1" pill>
                <b-link
                  class="small text-white text-decoration-none"
                  target="_blank"
                  :href="data.item.infoPV.minutesPdf"
                >
                  <font-awesome-icon icon="file-pdf" size="1x" />
                  Procès verbal
                </b-link>
              </b-badge>
              <b-badge v-if="data.item.infoPV.presencePdf" variant="success" class="mr-1" pill>
                <b-link
                  class="small text-white text-decoration-none"
                  target="_blank"
                  :href="data.item.infoPV.presencePdf"
                >
                  <font-awesome-icon icon="file-pdf" size="1x" />
                  Présence
                </b-link>
              </b-badge>
            </template>
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle mr-2"></b-spinner>
                <strong>Traitement en cours...</strong>
              </div>
            </template>
            <template #row-details="data">
              <div
                v-if="rowDetailsIsLoading && rowDetailsIsLoadingForMandateId == data.item.mandateId"
                class="text-center"
              >
                <b-spinner class="align-middle"></b-spinner>
              </div>
              <appDocsStatuses
                v-else
                :data-item="data.item"
                :mandates="mandates"
                :recipient-meeting-docs="recipientMeetingDocs"
              ></appDocsStatuses>
            </template>
          </b-table>
        </b-col>
        <b-col v-if="!showRepresentedRecipientsTable" cols="12" class="text-center">
          <b-col cols="4" class="m-auto d-flex justify-content-center">
            <b-pagination
              v-model="pagination.page"
              :total-rows="pagination.rowCount"
              :per-page="pagination.pageSize"
              aria-controls="mt-table"
              @input="fetchMandatesNewPage"
            ></b-pagination>
          </b-col>
        </b-col>
      </b-row>

      <appRepresentedRecipients
        v-if="showRepresentedRecipientsTable"
        :broadcast-pv-send-options="broadcastPvSendOptions"
        :broadcast-invitation-send-option="broadcastInvitationSendOption"
        :show-agency-preferred-broadcast="showPreferredBroadcast"
        :is-agency-preferred-broadcast-disabled="isPreferredBroadcastDisabled"
        :is-selected-preferred-invitation-disabled="isSelectedPreferredInvitationDisabled"
        :show-agency-preferred-invitation-broadcast-field="
          showAgencyPreferredInvitationBroadcastField
        "
        :meeting-status="meeting.status"
        :is-meeting-sending-invitation-to-asl-co-owners="isMeetingSendingInvitationToAslCoOwners"
        :is-meeting-sending-pv-to-asl-co-owners="isMeetingSendingPvToAslCoOwners"
      />

      <b-modal id="modal-ask-notification-type" size="md" hide-footer centered>
        <div
          aria-labelledby="swal2-title"
          aria-describedby="swal2-content"
          class="swal2-popup swal2-modal swal2-show"
          tabindex="-1"
          role="dialog"
          aria-live="assertive"
          aria-modal="true"
          style="display: flex;"
        >
          <div class="swal2-header d-flex align-items-center">
            <div
              class="swal2-icon swal2-question swal2-animate-question-icon"
              style="display: flex;"
            >
              <div class="swal2-icon-content">?</div>
            </div>
          </div>
          <h2 id="swal2-title" class="swal2-title" style="display: flex;">
            Pour les envois postaux, choisissez le mode d'envoi
          </h2>
        </div>
        <div class="swal2-actions">
          <button
            type="button"
            class="btn btn-warning btn-lg mb-2"
            @click="SEND_INVITATIONS(false)"
          >
            Manuellement par vos soins
          </button>
          <button
            type="button"
            class="swal2-confirm btn btn-primary btn-lg mb-2"
            @click="SEND_INVITATIONS(true)"
          >
            Automatiquement par l'imprimeur
          </button>
        </div>
      </b-modal>
    </b-container>
  </div>
  <appLoader v-else />
</template>

<script>
import MeetingMW from "@/middlewares/Meeting";
import MeetingORM from "@/store/orm-axios/models/Meeting";

import MediaMW from "@/middlewares/Media";
import PDFMW from "@/middlewares/PDF";

import useAlerts from "@/use/useAlerts.js";
import usePDFs from "@/use/usePDFs.js";

import ConfigurationMW from "@/middlewares/Configuration";
import ConfigurationORM from "@/store/orm-axios/models/Configuration";

import MandateORM from "@/store/orm-axios/models/Mandate";
import MandateMW from "@/middlewares/Mandate";

import links from "@/links/links";

import RecipientMeetingDocORM from "@/store/orm-axios/models/RecipientMeetingDoc";
import RecipientMeetingDocMW from "@/middlewares/RecipientMeetingDoc";

import { pdfsGenerateTypes } from "@/constants/pdf.js";
import { franceAndDOMCountries } from "@/constants/default.js";

import MajorityMW from "@/middlewares/Majority";
import MajorityORM from "@/store/orm-axios/models/Majority";

import UserMW from "@/middlewares/User";
import User from "@/store/orm-axios/models/User";

import InvoiceMW from "@/middlewares/Invoice";
import InvoiceORM from "@/store/orm-axios/models/Invoice";

import QRCode from "qrcode";
import { meetingMinutesNames } from "@/constants/meeting.js";

import MediaORM from "@/store/orm-axios/models/Media";

import BuildingORM from "@/store/orm-axios/models/Building";

import { lreRecordStatus } from "@/constants/mandate.js";

export default {
  components: {
    appGeneratePDFsProgress: () => import("@/components/snippets/GeneratePDFsProgress"),
    appDropzoneInvitationDocuments: () => import("@/components/snippets/DropzoneInvitationDoc"),
    appRepresentedRecipients: () =>
      import("@/views/meetings/MeetingDocumentsRepresentedRecipients.vue"),
    appDocsStatuses: () => import("./MeetingDocumentsDocsStatuses"),
  },
  data() {
    return {
      franceAndDOMCountries,
      isLoading: true,
      isTableLoading: true,
      sortBy: "name",
      sortDesc: false,
      buildingOwnersFields: [
        { key: "index", label: "Nᵒ" },
        { key: "gender", label: "Civilité", sortable: false },
        { key: "name", label: "Nom", sortable: false },
        { key: "volume", label: "Volume" },
        {
          key: "documents",
          label: "Documents individuels du copropriétaire",
        },
        {
          key: "agency_preferred_broadcast",
          label: "Canal de diffusion pour le PV",
          sortable: false,
        },
        {
          key: "agency_preferred_invitation_broadcast",
          label: "Canal de diffusion l'invitation",
          sortable: false,
        },
        // { key: "invitation", label: "Invitation", sortable: false },
        // { key: "pouvoir", label: "Pouvoir", sortable: false },
        // { key: "correspondence", label: "Correspondance", sortable: false },
        // { key: "order", label: "Ordre du jour", sortable: false },
        // { key: "pv", label: "Procès verbal", sortable: false },
        // { key: "presence", label: "Présence", sortable: false },
        { key: "cogeprint", label: "État de l'impression", sortable: false },
      ],
      buildingOwnersItems: [],
      currentPageBuildingOwners: 1,
      perPageBuildingOwners: 500,
      agendaPdf: null,
      zipPdf: null,
      minutesPdf: null,
      presencePdf: null,
      archivedDocPdf: null,
      signedPvMedia: null,
      signedPresenceMedia: null,
      additionalPrintMedia: [],
      selectedOwnerOrContact: null,
      isLoadingStartMeeting: false,
      currentMandate: null,
      selectedContactType: null,
      email: null,
      links,
      fileRecords: [],
      isSortable: false,
      pdfFooterMessage: "",
      pdfContactDetails: "",
      pdfAgencyEmail: "",
      selectedDocumentType: "",
      broadcastPvSendOptions: [
        {
          label: "Email",
          value: "99",
        },
        {
          label: "La Poste - Courrier recommandé avec AR",
          value: "00",
        },
        {
          label: "La Poste - Courrier simple",
          value: "04",
        },
        {
          label: "La Poste : Lettre prioritaire internationale",
          value: "05",
        },
        {
          label: "Colissimo France",
          value: "100",
        },
        {
          label: "Colissimo International",
          value: "101",
        },
        {
          label: "LRE",
          value: "97",
        },
      ],
      broadcastInvitationSendOption: [
        {
          label: "Envoyer manuellement",
          value: "98",
        },
        {
          label: "Email",
          value: "99",
        },
        {
          label: "La Poste - Courrier recommandé avec AR",
          value: "00",
        },
        {
          label: "La Poste - Courrier simple",
          value: "04",
        },
        {
          label: "La Poste : Lettre prioritaire internationale",
          value: "05",
        },
        {
          label: "Colissimo France",
          value: "100",
        },
        {
          label: "Colissimo International",
          value: "101",
        },
        {
          label: "LRE",
          value: "97",
        },
      ],
      docsPrintColorOptions: [
        {
          label: "Impression laser noir",
          value: "002",
        },
        {
          label: "Impression laser couleur",
          value: "004",
        },
        {
          label: "Impression jet d’encre",
          value: "006",
        },
      ],
      userId: Number(localStorage.getItem("userId")),
      rowDetailsIsLoading: false,
      rowDetailsIsLoadingForMandateId: null,
      mandates: [],
      pagination: {
        page: 1,
        pageSize: 10,
        rowCount: 0,
        pageCount: 0,
      },
      docsPrintColor: {
        label: "Impression laser noir",
        value: "002",
      },
      searchParams: "",
      isSearchInputFocused: false,
      hasSearchInputBeenReseted: true,
      boSortByName: "ASC",
      showRepresentedRecipientsTable: false,
      buildingsList: [],
    };
  },
  computed: {
    // ???: check if allResolutionsAreVoted before sending pv + documents ?
    meetingIsCompleted() {
      if (
        [
          this.meetingStatuses.completed,
          this.meetingStatuses.pvReadyToBeSent,
          this.meetingStatuses.pvInProgress,
          this.meetingStatuses.pvSent,
        ].includes(this.meeting.status)
      ) {
        return true;
      }
      return false;
    },
    pvReadyOrSent() {
      try {
        return [
          this.meetingStatuses.pvReadyToBeSent,
          this.meetingStatuses.pvInProgress,
          this.meetingStatuses.pvSent,
        ].includes(this.meeting.status);
      } catch {
        return false;
      }
    },
    pvReady() {
      try {
        return [this.meetingStatuses.pvReadyToBeSent, this.meetingStatuses.pvInProgress].includes(
          this.meeting.status
        );
      } catch {
        return false;
      }
    },
    pvSent() {
      try {
        return this.meeting.status == this.meetingStatuses.pvSent;
      } catch {
        return false;
      }
    },
    meetingId() {
      return this.$route.params.id;
    },
    allCountries() {
      return this.$store.getters.getCountries;
    },
    configuration() {
      // There will be only one configuration for each agency id
      return ConfigurationORM.query().where("agency_id", this.agencyId).first();
    },
    timezone() {
      return localStorage.getItem("timezone");
    },
    recipientMeetingDocs() {
      return RecipientMeetingDocORM.query().where().get();
    },
    isDefaultInvitationBroadcastNotSet() {
      let found = this.$_.find(this.buildingOwnersItems, item => {
        if (!item.isRecipientMeetingDoc || item.isRecipientMeetingDoc) {
          return item.agency_preferred_invitation_broadcast.length == 0;
        }
      });
      return found == undefined ? false : true;
    },
    meeting() {
      return MeetingORM.find(this.$route.params.id);
    },
    recipients() {
      let recipients = [];
      this.recipientMeetingDocs.forEach(recipientMeetingDoc => {
        recipients.push(recipientMeetingDoc.recipient);
      });
      return recipients;
    },
    majorities() {
      return MajorityORM.getters("getAll");
    },
    addInvitationIndividualFiles() {
      try {
        return [this.meetingStatuses.created, this.meetingStatuses.mandatesCreated].includes(
          this.meeting.status
        );
      } catch {
        return false;
      }
    },
    addPvIndividualFiles() {
      try {
        return [this.meetingStatuses.completed].includes(this.meeting.status);
      } catch {
        return false;
      }
    },
    user() {
      return User.find(this.userId) || {};
    },
    minuteEventMeetingStart() {
      return this.$_.find(this.meeting.minutes, minute => {
        return minute.event == meetingMinutesNames.meeting_start;
      });
    },
    pageIncrementalIndex() {
      return (this.pagination.page - 1) * this.pagination.pageSize;
    },
    invoices() {
      const invoices = InvoiceORM.query().where("meeting_id", this.meetingId).get();
      if (invoices.length > 0) {
        let toReturn = [];
        for (const invoice of invoices) {
          toReturn.push({
            file_path: links.API_WEB_APP_MEDIA_LINK(invoice.media.file_path),
            file_type: invoice.type,
          });
        }
        return toReturn;
      } else {
        return [];
      }
    },
    isSelectedPreferredInvitationDisabled() {
      const mandatesWithInvitation = MandateORM.query()
        .where("meeting_id", this.meetingId)
        .where(m => {
          return m.invitation_id != null;
        })
        .get();

      return !(
        (mandatesWithInvitation.length == 0 &&
          this.meeting.status == this.meetingStatuses.created) ||
        this.meeting.status == this.meetingStatuses.mandatesCreated
      );
    },
    showPreferredBroadcast() {
      return (
        this.minutesPdf &&
        (this.meetingIsCompleted ||
          this.meeting.status == this.meetingStatuses.archived ||
          this.meeting.status == this.meetingStatuses.toBeDeleted)
      );
    },
    isPreferredBroadcastDisabled() {
      return !this.minutesPdf || this.meeting.status != this.meetingStatuses.completed;
    },
    sendInvitationOrPvToAslCoOwners() {
      if (
        this.meeting.send_invitation_files_to_asl_co_owners ||
        this.meeting.send_pv_files_to_asl_co_owners
      ) {
        return true;
      } else {
        return false;
      }
    },
    showAgencyPreferredInvitationBroadcastField() {
      return (
        this.meeting.status == this.meetingStatuses.mandatesCreated ||
        (this.meeting.agenda_media_id && this.meeting.agenda_media_id != null)
      );
    },
    isMeetingSendingInvitationToAslCoOwners() {
      if (this.meeting.send_invitation_files_to_asl_co_owners) {
        return true;
      }
      return false;
    },
    isMeetingSendingPvToAslCoOwners() {
      if (this.meeting.send_pv_files_to_asl_co_owners) {
        return true;
      }
      return false;
    },
    isMeetingAsl() {
      const building = BuildingORM.find(this.meeting.building_id);
      if (building && building.asl) {
        return true;
      }
      return false;
    },
  },
  watch: {
    fileRecords(newValue) {
      this.updatePdfPosition(newValue);
    },
    configuration() {
      this.getPdfConfigs();
    },
    meeting(newValue, oldValue) {
      if (newValue["is_scheduled_to_generate_pdfs"] != oldValue["is_scheduled_to_generate_pdfs"]) {
        //NOTE: Reset and get updated/created meeting documents
        this.buildingOwnersItems = [];
        MeetingMW.GET_DOCUMENTS(this.$route.params.id).then(async () => {
          await this.getMandates();
          this.processData();
        });
      }
    },
  },
  async created() {
    this.isLoading = true;
    this.isTableLoading = true;

    await RecipientMeetingDocMW.GET_BY_MEETING(this.$route.params.id, true, 0);

    await MeetingMW.GET_DOCUMENTS(this.$route.params.id);

    await this.getMandates();
    this.processData();
    await ConfigurationMW.FETCH(this.agencyId).then(() => {
      this.getPdfConfigs();
    });

    if (
      this.meeting.status != this.meetingStatuses.created &&
      this.meeting.status != this.meetingStatuses.mandatesCreated &&
      this.meeting.status != this.meetingStatuses.invitationReadyToBeSent &&
      this.meeting.status != this.meetingStatuses.invitationInProgress
    ) {
      await InvoiceMW.GET_BY_MEETING(this.meeting.id);
    }

    this.isLoading = false;
    this.isTableLoading = false;

    if (this.timezone) {
      this.$moment.tz.setDefault(this.timezone);
    }

    if (this.isMeetingAsl) {
      await this.getBuildingsList();
    }
  },
  methods: {
    async getMandates() {
      // NOTE: Mandates will be get without saving them to vuex store
      const queryParams = {
        meetingId: this.meeting.id,
        page: this.pagination.page,
        itemsPerPage: this.pagination.pageSize,
        orderByBoName: this.boSortByName,
        searchParams: this.searchParams,
      };
      await MandateMW.GET_DOCS_BY_MEETING(queryParams).then(res => {
        if (res.response && res.response.data && res.response.data["hydra:member"]) {
          this.mandates = res.response.data["hydra:member"];
        }
        let totalItems = res.response.data["hydra:totalItems"];
        this.pagination.rowCount = totalItems;
      });
    },
    processData() {
      const meeting = this.meeting;

      this.processMeetingData(meeting);

      this.initFileRecords(meeting);

      this.processMandatesData();

      this.sortBuildingOwnersItems();
    },
    processMeetingData(meeting) {
      if (meeting.agenda_media_id) {
        this.agendaPdf = links.API_WEB_APP_MEDIA_LINK(
          MediaORM.find(meeting.agenda_media_id).file_path
        );
      }

      if (meeting.zip_media_id) {
        this.zipPdf = links.API_WEB_APP_MEDIA_LINK(MediaORM.find(meeting.zip_media_id).file_path);
      }

      if (meeting.minutes_media_id) {
        this.minutesPdf = links.API_WEB_APP_MEDIA_LINK(
          MediaORM.find(meeting.minutes_media_id).file_path
        );
      }

      if (meeting.presence_media_id) {
        this.presencePdf = links.API_WEB_APP_MEDIA_LINK(
          MediaORM.find(meeting.presence_media_id).file_path
        );
      }
      if (meeting.archived_doc_details_media_id) {
        this.archivedDocPdf = links.API_WEB_APP_MEDIA_LINK(
          MediaORM.find(meeting.archived_doc_details_media_id).file_path
        );
      }

      if (meeting.signed_pv_media_id) {
        this.signedPvMedia = links.API_WEB_APP_MEDIA_LINK(
          MediaORM.find(meeting.signed_pv_media_id).file_path
        );
      }

      let signedPresenceMedia = MediaORM.query()
        .where("signed_presence_meeting_id", this.meetingId)
        .first();
      this.signedPresenceMedia = signedPresenceMedia
        ? links.API_WEB_APP_MEDIA_LINK(signedPresenceMedia.file_path)
        : null;

      if (meeting.docs_print_color) {
        this.docsPrintColor = meeting.docs_print_color;
      }
    },
    processBuildingOwnerData(mandate, BuildingOwnerData) {
      let BO = {};

      BO.name = this.getPersonName({}, BuildingOwnerData);
      BO.gender = this.getPersonGender({}, BuildingOwnerData);
      BO.email = BuildingOwnerData.email;
      BO.country = BuildingOwnerData.country;

      BO.mandateId = mandate.id;

      BO.documents = {};
      if (mandate.invitation) {
        BO.documents.invitationPdf = links.API_WEB_APP_MEDIA_LINK(mandate.invitation.file_path);
      }

      if (mandate.pouvoir) {
        BO.documents.pouvoirPdf = links.API_WEB_APP_MEDIA_LINK(mandate.pouvoir.file_path);
      }

      if (mandate.voting_form) {
        BO.documents.vbcPdf = links.API_WEB_APP_MEDIA_LINK(mandate.voting_form.file_path);
      }

      if (mandate.merged_media) {
        BO.documents.mergedPdf = links.API_WEB_APP_MEDIA_LINK(mandate.merged_media.file_path);
      }
      if (mandate.merged_pv_media) {
        BO.documents.mergedPvMedia = links.API_WEB_APP_MEDIA_LINK(
          mandate.merged_pv_media.file_path
        );
      }
      if (mandate.individual_additional_file_invitation) {
        BO.documents.individualAdditionalFileInvitation = [];
        mandate.individual_additional_file_invitation.forEach(file => {
          BO.documents.individualAdditionalFileInvitation.push({
            id: file.id,
            path: links.API_WEB_APP_MEDIA_LINK(file.file_path),
            label: file.label,
          });
        });
      }
      if (mandate.individual_additional_files_pv) {
        BO.documents.individualAdditionalFilesPv = [];
        mandate.individual_additional_files_pv.forEach(file => {
          BO.documents.individualAdditionalFilesPv.push({
            id: file.id,
            path: links.API_WEB_APP_MEDIA_LINK(file.file_path),
            label: file.label,
          });
        });
      }

      BO.docsStatus = mandate.docs_status;

      // Get broadcastOption for mandate
      let broadcastPvSendOptions = this.broadcastPvSendOptions.filter(
        broadcastOption => broadcastOption.value === mandate.agency_preferred_broadcast
      );
      let broadcastInvitationSendOptionMandate = this.broadcastInvitationSendOption.filter(
        broadcastOption => broadcastOption.value === mandate.agency_preferred_invitation_broadcast
      );
      BO.agency_preferred_broadcast = broadcastPvSendOptions;
      BO.agency_preferred_invitation_broadcast = broadcastInvitationSendOptionMandate;
      BO.lre_agreement = BuildingOwnerData.settings
        ? BuildingOwnerData.settings.lre_agreement
        : null;
      BO.building_asl_id = BuildingOwnerData.building_asl_id;

      this.buildingOwnersItems.push(BO);
    },
    processRecipientData(mandate, BuildingOwnerData) {
      // Filter only the recipientMeetingDocs from current mandate
      const recipientMeetingDocs = this.recipientMeetingDocs.filter(
        recipientMeetingDoc => recipientMeetingDoc.mandate_id === mandate.id
      );

      recipientMeetingDocs.forEach(recipientMeetingDoc => {
        let recipient = {};

        // Get broadcastOption for mandate
        const broadcastSendOptionRecipient = this.broadcastPvSendOptions.filter(
          broadcastOption =>
            broadcastOption.value === recipientMeetingDoc.agency_preferred_broadcast
        );
        const broadcastInvitationSendOptionRecipient = this.broadcastInvitationSendOption.filter(
          broadcastOption =>
            broadcastOption.value === recipientMeetingDoc.agency_preferred_invitation_broadcast
        );

        recipient.buildingOwnerName = this.getPersonName({}, BuildingOwnerData);

        recipient.name = this.getPersonName({}, recipientMeetingDoc.recipient);
        recipient.email = recipientMeetingDoc.recipient.email;
        recipient.country = recipientMeetingDoc.recipient.country;

        recipient.recipientMeetingDocId = recipientMeetingDoc.id;
        recipient.documents = [];
        recipient.docsStatus = recipientMeetingDoc.docs_status;
        recipient.agency_preferred_broadcast = broadcastSendOptionRecipient;
        recipient.agency_preferred_invitation_broadcast = broadcastInvitationSendOptionRecipient;
        recipient.isRecipientMeetingDoc = true;

        if (recipientMeetingDoc.merged_invitation_media) {
          recipient.documents.mergedPdf = links.API_WEB_APP_MEDIA_LINK(
            recipientMeetingDoc.merged_invitation_media.file_path
          );
        }

        if (recipientMeetingDoc.merged_pv_media) {
          recipient.documents.mergedPvMedia = links.API_WEB_APP_MEDIA_LINK(
            recipientMeetingDoc.merged_pv_media.file_path
          );
        }

        this.buildingOwnersItems.push(recipient);
      });
    },
    processMandatesData() {
      for (const mandate of this.mandates) {
        const BuildingOwnerData = this.findBuildingOwnerDataByMandateId(mandate.id);

        this.processBuildingOwnerData(mandate, BuildingOwnerData);
        this.processRecipientData(mandate, BuildingOwnerData);
      }
    },
    sortBuildingOwnersItems() {
      this.buildingOwnersItems = this.$_.sortBy(this.buildingOwnersItems, boItem => {
        return boItem.buildingOwnerName ? boItem.buildingOwnerName : boItem.name;
      });
    },
    async fetchMandatesNewPage() {
      this.isTableLoading = true;
      await this.getMandates();

      this.buildingOwnersItems = [];
      this.processMandatesData();
      this.sortBuildingOwnersItems();

      this.isTableLoading = false;
    },

    selectSignedPv() {
      this.selectedDocumentType = "signed_pv";
      this.$refs.vueFileAgent.$el.firstChild.lastChild.click();
    },
    selectSignedPresence() {
      this.selectedDocumentType = "signed_presence";
      this.$refs.vueFileAgent.$el.firstChild.lastChild.click();
    },
    selectAdditionalPrintMedia() {
      this.selectedDocumentType = "additional";
    },

    initFileRecords(meeting) {
      var initFileRecordsArray = [];

      // NOTE: SignedPV
      if (meeting.signed_pv_media_id) {
        initFileRecordsArray.push(MediaORM.find(meeting.signed_pv_media_id));
      }
      // NOTE: SignedPresence
      let signedPresenceMedia = MediaORM.query()
        .where("signed_presence_meeting_id", this.meetingId)
        .first();
      if (signedPresenceMedia) {
        initFileRecordsArray.push(signedPresenceMedia);
      }

      // NOTE: additional_print_media, signed_presence_media, etc
      const otherMedia = MediaORM.getters("getByMeetingId")(meeting.id);
      initFileRecordsArray = initFileRecordsArray.concat(otherMedia);

      // Create fileRecords for upload zone with existing media
      for (const media of initFileRecordsArray) {
        this.createPdfFileRecord(media);
      }
      // Sort by media position
      this.fileRecords = this.$_.sortBy(this.fileRecords, fileRecord => {
        return fileRecord.media.position;
      });
    },
    createPdfFileRecord(media) {
      let mediaName = media.label ? media.label : media.name || media.filename;
      let defaultRecord = {};
      var file = new File([0], mediaName + ".pdf", {
        type: "application/pdf",
      });
      defaultRecord["media"] = media;
      defaultRecord["file"] = file;
      if (media.file_path) {
        defaultRecord["url"] = links.API_WEB_APP_MEDIA_LINK(media.file_path);
      }
      this.fileRecords.push(defaultRecord);

      // if it's the case, update local meeting signed documents data
      if (media.signed_pv_meeting_id) this.signedPvMedia = media;
      if (media.signed_presence_meeting_id) this.signedPresenceMedia = media;
    },

    updatePdfPosition(fileRecords) {
      let media = [];
      fileRecords.forEach((record, index) => {
        record.media.position = index + 1;
        media.push(record.media);
      });
      if (media.length > 0) {
        MediaMW.MEDIA_UPDATE_PDF_POSITION({ orderMediaPosition: media });
      }
    },

    filesSelected(fileRecordsNewlySelected) {
      let files = [];
      fileRecordsNewlySelected.forEach(fileRecord => {
        files.push(fileRecord.file);
      });
      // Filter fileRecords in order to remove the fileRecordsNewlySelected because fileRecords will be populated from DB in addFiles()
      this.fileRecords = this.fileRecords.filter(val => !fileRecordsNewlySelected.includes(val));
      this.addFiles(files);
    },
    async addFiles(data) {
      var errors = "";
      let files = data;
      files.forEach(file => {
        if (file.type != "application/pdf") {
          errors += "\n" + file.name;
        }
      });
      if (errors != "") {
        this.$swal({
          icon: "error",
          title: "Erreur de téléchargement PDF !",
          text: "Certains fichiers n'ont pas été acceptés. Ils doivent être des fichiers PDF.",
          showConfirmButton: false,
          timer: 3500,
        });
        return;
      }
      if (files.length > 0) {
        await MediaMW.MEDIA_CHECK_PDF_FORMAT(
          files,
          "meeting",
          this.meeting.id,
          this.selectedDocumentType
        )
          .then(res => {
            if (res.response.data.invalidFiles.length > 0) {
              this.$swal({
                icon: "error",
                title: "Erreur de téléchargement PDF !",
                text: this.displayInvalidFiles(res.response.data.invalidFiles),
                showConfirmButton: true,
                timer: 15000,
              });
            }
            if (res.response.data.validFiles.length > 0) {
              res.response.data.validFiles.forEach(file => {
                this.createPdfFileRecord(file);
              });
            }
          })
          .catch(() => {
            this.$swal(this.alertError());
          });
      }
    },
    displayInvalidFiles(invalidFiles) {
      let arrayToShow = [];
      this.$_.each(invalidFiles, file => {
        arrayToShow.push(file.name + " a un format différent de celui A4 ");
      });
      return arrayToShow;
    },

    fileChanged(fileRecord) {
      // On change file name
      fileRecord.media.label = fileRecord.name().split(".")[0];
      this.updateMediaLabel(fileRecord.media);
    },
    updateMediaLabel(media) {
      MediaMW.PUT(media)
        .then(res => {})
        .catch(err => {
          console.log("err update media", err);
        });
    },

    onBeforeDelete(fileRecord) {
      this.$swal(this.alertQuestion())
        .then(res => {
          if (res.value) {
            this.$refs.vueFileAgent.deleteFileRecord(fileRecord); // will trigger 'delete' event
          }
        })
        .catch(() => {
          console.log("Cancel");
        });
    },
    fileDeleted(fileRecord) {
      this.DELETE_MEDIA(fileRecord.media);
    },
    DELETE_MEDIA(media) {
      MediaMW.DELETE(media)
        .then(() => {
          // if it's the case, remove local meeting signed documents data
          if (media.signed_pv_meeting_id) this.signedPvMedia = null;
          if (media.signed_presence_meeting_id) this.signedPresenceMedia = null;
        })
        .catch(() => {
          this.$swal(this.alertError());
        });
    },

    toggleDetails(data) {
      data.toggleDetails();

      if (data.item.mandateId && !data.detailsShowing) {
        this.rowDetailsIsLoading = true;
        this.rowDetailsIsLoadingForMandateId = data.item.mandateId;
        this.fetchDocRequestsForMandate(data.item.mandateId);
      }
    },
    async createPvAntetAndUpdatePvReadyToSend() {
      let self = this;
      this.$swal(this.alertQuestion())
        .then(res => {
          if (res.value) {
            self.GENERATE_PDF_PRIMARY_FUNCTION(self.CREATE_PV_ANTET_PV_READY_TO_SEND);
          }
        })
        .catch(err => {
          console.error(err);
          self.$swal(self.alertError());
        });
    },
    getPdfConfigs() {
      // Get current pdf_configs value
      if (this.configuration && this.configuration.settings.length > 0) {
        this.configuration.settings.forEach(element => {
          if (element.pdf_footer) {
            this.pdfFooterMessage = element.pdf_footer;
          }
          if (element.pdf_contact_details) {
            this.pdfContactDetails = element.pdf_contact_details;
          }
          if (element.agency_email) {
            this.pdfAgencyEmail = element.agency_email;
          }
        });
      }
    },
    getAgencyNameConfigs() {
      if (this.configuration && this.configuration.settings.length > 0) {
        const found = this.configuration.settings.find(
          element => element.agency_name && element.agency_name != ""
        );
        if (found) {
          return found.agency_name;
        }
      }
      return null;
    },
    getAgencyEmailConfigs() {
      if (this.configuration && this.configuration.settings.length > 0) {
        const found = this.configuration.settings.find(
          element => element.agency_email && element.agency_email != ""
        );
        if (found) {
          return found.agency_email;
        }
      }
      return null;
    },
    toUtc(date) {
      /*
       * This function will make sure that data that has no timezone will have UTC
       */
      let utcFormatted = date;
      if (date) {
        utcFormatted = this.$moment
          .parseZone(date)
          .utcOffset(0, true)
          .format("YYYY-MM-DDTHH:mm:ssZ");
      }
      return utcFormatted;
    },
    updatePrefferedBroadcast(agencyPreferredBroadcast, item) {
      if (item.recipientMeetingDocId) {
        let recipientMeetingDoc = RecipientMeetingDocORM.query()
          .where("id", item.recipientMeetingDocId)
          .first();
        recipientMeetingDoc.agency_preferred_broadcast = agencyPreferredBroadcast.value;
        RecipientMeetingDocMW.PUT(recipientMeetingDoc);
      } else {
        let mandate = MandateORM.query().where("id", item.mandateId).first();
        mandate.agency_preferred_broadcast = agencyPreferredBroadcast.value;
        MandateMW.PUT(mandate);
      }
    },
    detailsClassFn(item) {
      if (item && item.isRecipientMeetingDoc) {
        return "bg-info-light";
      }
    },
    SEND_INVITATIONS(isAutomatically) {
      if (isAutomatically) {
        this.isLoading = true;
        MeetingMW.MEETING_INVITATION_READY_TO_SEND(this.meetingId)
          .then(() => {
            this.isLoading = false;
          })
          .catch(err => {
            this.isLoading = false;
            const { alertErrorAndReport } = useAlerts();
            alertErrorAndReport(
              err,
              "Sending Notification",
              "Can't change meeting notification status"
            );
          });
      } else {
        this.isLoading = true;
        MandateMW.MANDATE_GET_MANUAL_INVITATION_BROADCAST(this.meetingId).then(async res => {
          let mandates = res["hydra:member"];
          for (const mandate of mandates) {
            let foundMandateIndex = this.$_.findIndex(this.buildingOwnersItems, item => {
              return item.mandateId == mandate.id;
            });
            if (foundMandateIndex == "-1") continue;
            // Get broadcast option
            let broadcastInvitationSendOptionMandate = this.broadcastInvitationSendOption.filter(
              broadcastOption =>
                broadcastOption.value === mandate.agency_preferred_invitation_broadcast
            );
            // Set the default agency_preferred_invitation_broadcast
            this.buildingOwnersItems[
              foundMandateIndex
            ].agency_preferred_invitation_broadcast = broadcastInvitationSendOptionMandate;
          }
          this.isLoading = false;
        });
      }
    },
    updatePrefferedInvitationBroadcast(agencyPreferredBroadcast, item) {
      let mandate = MandateORM.query().where("id", item.mandateId).first();
      mandate.agency_preferred_invitation_broadcast = agencyPreferredBroadcast.value;
      MandateMW.PUT(mandate);
    },
    agencyPreferredInvitationBroadcastOptions(mandate) {
      let isFrOrDOMCountry = this.$_.contains(this.franceAndDOMCountries, mandate.country);
      let returnOptions = this.broadcastInvitationSendOption;
      if (!mandate.email) {
        returnOptions = returnOptions.filter(b => b.label != "Email" && b.label != "LRE");
      }
      if (!isFrOrDOMCountry) {
        returnOptions = returnOptions.filter(
          b =>
            b.label != "La Poste - Courrier recommandé avec AR" &&
            b.label != "La Poste - Courrier simple" &&
            b.label != "Colissimo France"
        );
      }
      if (isFrOrDOMCountry) {
        returnOptions = returnOptions.filter(
          b =>
            b.label != "La Poste : Lettre prioritaire internationale" &&
            b.label != "Colissimo International"
        );
      }
      return returnOptions;
    },
    agencyPreferredPvBroadcastOptions(mandate) {
      let isFrOrDOMCountry = this.$_.contains(this.franceAndDOMCountries, mandate.country);
      let returnOptions = this.broadcastPvSendOptions;
      if (!mandate.email) {
        returnOptions = returnOptions.filter(b => b.label != "Email" && b.label != "LRE");
      }
      if (!isFrOrDOMCountry) {
        returnOptions = returnOptions.filter(
          b =>
            b.label != "La Poste - Courrier recommandé avec AR" &&
            b.label != "La Poste - Courrier simple" &&
            b.label != "Colissimo France"
        );
      }
      if (isFrOrDOMCountry) {
        returnOptions = returnOptions.filter(
          b =>
            b.label != "La Poste : Lettre prioritaire internationale" &&
            b.label != "Colissimo International"
        );
      }
      return returnOptions;
    },
    async getDefaultAgencyPreferredInvitationBroadcast() {
      this.isLoading = true;
      await MandateMW.MANDATE_GET_DEFAULT_INVITATION_BROADCAST(this.meetingId).then(async res => {
        let mandates = res["hydra:member"];
        mandates.forEach(mandate => {
          let foundMandateIndex = this.$_.findIndex(this.buildingOwnersItems, item => {
            return item.mandateId == mandate.id;
          });
          // Get broadcast option
          let broadcastInvitationSendOptionMandate = this.broadcastInvitationSendOption.filter(
            broadcastOption =>
              broadcastOption.value === mandate.agency_preferred_invitation_broadcast
          );
          // Set the default agency_preferred_invitation_broadcast
          this.buildingOwnersItems[
            foundMandateIndex
          ].agency_preferred_invitation_broadcast = broadcastInvitationSendOptionMandate;
        });
      });
      this.isLoading = false;
    },
    async GENERATE_PDFS(meeting) {
      const {
        generateTemplateOrder,
        generateTemplateInvitation,
        generateTemplateInvitationAntet,
        generateTemplatePouvoir,
        generateTemplateVBC,
      } = usePDFs();

      let recipients = [];
      await RecipientMeetingDocMW.GET_BY_MEETING(this.$route.params.id, false)
        .then(res => {
          if (res["hydra:member"]) {
            const recipientMeetingDocs = res["hydra:member"];
            recipientMeetingDocs.forEach(recipientMeetingDoc => {
              recipients.push(recipientMeetingDoc.recipient);
            });
          }
        })
        .catch(() => {
          this.$store.state.showLoaderOverlay = false;
        });
      // NOTE: Recipients invitationAntet
      let recipientsWithTemplates = [];
      for (let recipient of recipients) {
        let recipientWithTemplates = {
          recipientId: recipient.id,
          templates: {},
        };

        await generateTemplateInvitationAntet(
          meeting,
          null,
          this.pdfFooterMessage,
          this.allCountries,
          this.pdfContactDetails,
          recipient
        ).then(template => {
          // 1. Invitation Antet PDF
          recipientWithTemplates.templates.invitationAntet = template;
        });

        recipientsWithTemplates.push(recipientWithTemplates);
      }

      let meetingWithTemplates = {
        meetingId: meeting.id,
        templates: {},
      };

      await generateTemplateOrder(meeting, this.pdfFooterMessage).then(template => {
        meetingWithTemplates.templates.order = template;
      });

      // NOTE: Double check here for correspondance in order to prevent calling Majorities multiple times in the for loop below
      if (meeting.allowed_modalities.correspondence) {
        await MajorityMW.FETCH();
      }

      const mandates = MandateORM.query()
        .where("meeting_id", this.meetingId)
        .with("building_owner.building_owner_building_lots.building_lot")
        .with(
          "building_owner.building_owner_building_lots.building_owner_voting_rights.voting_category"
        )
        .get();

      let mandatesWithTemplates = [];
      for (let mandate of mandates) {
        let mandateWithTemplates = {
          mandateId: mandate.id,
          templates: {},
        };

        await generateTemplateInvitationAntet(
          meeting,
          mandate,
          this.pdfFooterMessage,
          this.allCountries,
          this.pdfContactDetails
        ).then(template => {
          // 1. Invitation Antet PDF
          mandateWithTemplates.templates.invitationAntet = template;
        });

        let qrCodeBase64Image = this.getQrCodeBase64Image(mandate.building_owner_id);

        await generateTemplateInvitation(
          meeting,
          mandate,
          this.pdfFooterMessage,
          this.allCountries,
          this.pdfContactDetails,
          false,
          qrCodeBase64Image
        ).then(template => {
          // 1. Invitation PDF
          mandateWithTemplates.templates.invitation = template;
        });

        if (meeting.allowed_modalities.physical || meeting.allowed_modalities.video) {
          await generateTemplatePouvoir(
            meeting,
            mandate,
            this.pdfFooterMessage,
            qrCodeBase64Image
          ).then(template => {
            // 2. Pouvoir PDF
            mandateWithTemplates.templates.pouvoir = template;
          });
        }
        if (meeting.allowed_modalities.correspondence) {
          await generateTemplateVBC(
            meeting,
            mandate,
            this.pdfFooterMessage,
            this.allCountries,
            this.majorities,
            this.pdfContactDetails,
            this.pdfAgencyEmail
          ).then(template => {
            // 3. Correspondence PDF
            mandateWithTemplates.templates.correspondence = template;
          });
        }

        mandatesWithTemplates.push(mandateWithTemplates);
      }

      const pdfsToGenerateRowData = {
        meetingId: meeting.id,
        meetingWithTemplates,
        mandatesWithTemplates,
        recipientsWithTemplates,
        generateType: pdfsGenerateTypes.generate_type_meeting_and_mandates_pdfs,
      };
      PDFMW.CREATE_PDFS_TO_GENERATE_ROW(pdfsToGenerateRowData)
        .then(() => {
          this.$store.state.showLoaderOverlay = false;
        })
        .catch(err => {
          this.generatePdfCommonError(err);
        });
    },
    async GENERATE_PDF_FOR_ONE_MANDATE(meeting) {
      let isforOneMandate = true;
      const {
        generateTemplateOrder,
        generateTemplateInvitation,
        generateTemplateInvitationAntet,
        generateTemplatePouvoir,
        generateTemplateVBC,
        downloadDialog,
      } = usePDFs();

      let pdfToGenerate = {};

      const mandate = MandateORM.query()
        .where("meeting_id", this.meetingId)
        .with("building_owner.building_owner_building_lots.building_lot")
        .with(
          "building_owner.building_owner_building_lots.building_owner_voting_rights.voting_category"
        )
        .first();

      try {
        await generateTemplateInvitationAntet(
          meeting,
          mandate,
          this.pdfFooterMessage,
          this.allCountries,
          this.pdfContactDetails,
          isforOneMandate
        ).then(template => {
          // 1. Invitation Antet PDF
          pdfToGenerate.invitationAntet = template;
        });

        let qrCodeBase64Image = this.getQrCodeBase64Image(mandate.building_owner_id);

        await generateTemplateInvitation(
          meeting,
          mandate,
          this.pdfFooterMessage,
          this.allCountries,
          this.pdfContactDetails,
          isforOneMandate,
          qrCodeBase64Image
        ).then(template => {
          // 1. Invitation PDF
          pdfToGenerate.invitation = template;
        });

        if (meeting.allowed_modalities.physical || meeting.allowed_modalities.video) {
          await generateTemplatePouvoir(
            meeting,
            mandate,
            this.pdfFooterMessage,
            qrCodeBase64Image
          ).then(template => {
            // 2. Pouvoir PDF
            pdfToGenerate.pouvoir = template;
          });
        }
        if (meeting.allowed_modalities.correspondence) {
          await MajorityMW.FETCH();
          await generateTemplateVBC(
            meeting,
            mandate,
            this.pdfFooterMessage,
            this.allCountries,
            this.majorities,
            this.pdfContactDetails,
            this.pdfAgencyEmail
          ).then(template => {
            // 3. Correspondence PDF
            pdfToGenerate.correspondence = template;
          });
        }

        await generateTemplateOrder(meeting, this.pdfFooterMessage).then(template => {
          // 4. Order PDF
          pdfToGenerate.order = template;
        });

        PDFMW.MANDATE_PDF_CREATE_MERGE(meeting.id, pdfToGenerate, mandate.id)
          .then(mergedPDFs => {
            this.$store.state.showLoaderOverlay = false;
            downloadDialog(
              mergedPDFs.response.data.document,
              "TEST " +
                this.getPersonName({
                  personId: mandate.building_owner_id,
                }),
              "pdf"
            );
          })
          .catch(err => {
            this.generatePdfCommonError(err);
          });
      } catch (err) {
        this.generatePdfCommonError(err);
      }
    },
    async CREATE_PV_ANTET_PV_READY_TO_SEND(meeting) {
      const { generateTemplatePvAntet } = usePDFs();

      let recipients = [];
      await RecipientMeetingDocMW.GET_BY_MEETING(this.$route.params.id, false)
        .then(res => {
          if (res["hydra:member"]) {
            const recipientMeetingDocs = res["hydra:member"];
            recipientMeetingDocs.forEach(recipientMeetingDoc => {
              recipients.push(recipientMeetingDoc.recipient);
            });
          }
        })
        .catch(() => {
          this.$store.state.showLoaderOverlay = false;
        });
      /** Recipients */
      let recipientsWithTemplates = [];
      for (let recipient of recipients) {
        let recipientWithTemplates = {
          recipientId: recipient.id,
          templates: {},
        };

        await generateTemplatePvAntet(
          meeting,
          null,
          recipient,
          this.pdfFooterMessage,
          this.allCountries,
          this.pdfContactDetails
        ).then(template => {
          recipientWithTemplates.templates.pvAntet = template;
        });

        recipientsWithTemplates.push(recipientWithTemplates);
      }

      /** Mandates */
      const mandates = MandateORM.query()
        .where("meeting_id", this.meetingId)
        .with("building_owner.building_owner_building_lots.building_lot")
        .with(
          "building_owner.building_owner_building_lots.building_owner_voting_rights.voting_category"
        )
        .get();

      let mandatesWithTemplates = [];
      for (let mandate of mandates) {
        let mandateWithTemplates = {
          mandateId: mandate.id,
          templates: {},
        };

        await generateTemplatePvAntet(
          meeting,
          mandate,
          null,
          this.pdfFooterMessage,
          this.allCountries,
          this.pdfContactDetails
        ).then(template => {
          mandateWithTemplates.templates.pvAntet = template;
        });

        mandatesWithTemplates.push(mandateWithTemplates);
      }

      const pdfsToGenerateRowData = {
        meetingId: this.meetingId,
        mandatesWithTemplates,
        recipientsWithTemplates,
        generateType: pdfsGenerateTypes.generate_type_mandates_and_recipients_pv_antet_pdfs,
      };
      await PDFMW.CREATE_PDFS_TO_GENERATE_ROW(pdfsToGenerateRowData)
        .then(() => {
          this.$store.state.showLoaderOverlay = false;
        })
        .catch(err => {
          this.$store.state.showLoaderOverlay = false;
          const { alertErrorAndReport } = useAlerts();
          alertErrorAndReport(err, "", "");
        });
    },
    async CREATE_MERGE_PV_PREVIEW(meeting) {
      const { generateTemplatePvAntet, downloadDialog } = usePDFs();

      const mandate = MandateORM.query()
        .where("meeting_id", this.meetingId)
        .with("building_owner.building_owner_building_lots.building_lot")
        .with(
          "building_owner.building_owner_building_lots.building_owner_voting_rights.voting_category"
        )
        .first();

      let pdfToGenerate = {};

      await generateTemplatePvAntet(
        meeting,
        mandate,
        null,
        this.pdfFooterMessage,
        this.allCountries,
        this.pdfContactDetails
      ).then(template => {
        pdfToGenerate.pvAntet = template;
      });
      await PDFMW.MANDATE_PDF_CREATE_MERGE_PV(mandate.id, meeting.id, pdfToGenerate).then(
        mergedPDF => {
          downloadDialog(
            mergedPDF.response.data.document,
            "TEST MERGED PV " +
              this.getPersonName({
                personId: mandate.building_owner_id,
              }),
            "pdf"
          );
        }
      );

      this.$store.state.showLoaderOverlay = false;
    },
    // use this to not repeat first steps for every function
    GENERATE_PDF_PRIMARY_FUNCTION(generatePdfSecondaryFunction) {
      // check if the Agency has a logo
      const idLogoAgency = localStorage.getItem("userMediaId");
      if (idLogoAgency) {
        this.$store.state.showLoaderOverlay = true;
        MeetingMW.GET_WITH_RELATED_MEDIA(this.meetingId)
          .then(meeting => {
            MediaMW.GET_LOGO_IN_BASE64(idLogoAgency)
              .then(logo => {
                meeting["logoInBase64"] = logo["file_as_base64_string"];

                // call secondary function
                generatePdfSecondaryFunction(meeting);
              })
              .catch(err => {
                console.error(err);
                this.$store.state.showLoaderOverlay = false;
                const { alertErrorAndReport } = useAlerts();
                alertErrorAndReport(
                  err,
                  "Impossible d'extraire le logo !",
                  "Veuillez vérifier votre connexion Internet."
                );
              });
          })
          .catch(err => {
            console.error(err);
            this.$store.state.showLoaderOverlay = false;
            const { alertErrorAndReport } = useAlerts();
            alertErrorAndReport(
              err,
              "Impossible d'extraire les informations sur la Convocation !",
              "Veuillez vérifier votre connexion Internet."
            );
          });
      } else {
        this.$swal({
          icon: "warning",
          title: "Vous n'avez spécifié aucun logo pour votre agence !",
          html: "Accédez au profil et ajoutez un logo d'agence.",
          showConfirmButton: true,
          showCancelButton: true,
          confirmButtonText: "Ajouter le logo",
          confirmButtonClass: "btn btn-primary btn-lg",
          cancelButtonText: "Pas maintenant",
        }).then(res => {
          if (res.value) {
            this.$router.push({
              name: "profile",
            });
          }
        });
      }
    },
    generatePdfCommonError(err) {
      console.error(err);
      this.$store.state.showLoaderOverlay = false;
      const { alertErrorAndReport } = useAlerts();
      alertErrorAndReport(err, "", "");
    },
    uploadIndividualFiles(mandateId, type) {
      if (type == "invitation") {
        this.$refs["individualInvitationFile-" + mandateId].click();
      } else {
        this.$refs["individualPvFile-" + mandateId].click();
      }
    },
    async uploadedIndividualFiles(e, mandateId, type) {
      let foundIndex = this.$_.findIndex(this.buildingOwnersItems, item => {
        return item.mandateId == mandateId;
      });
      if (foundIndex != "-1" && e.target && e.target.files.length > 0) {
        await MediaMW.MEDIA_CHECK_PDF_FORMAT(e.target.files, "mandate", mandateId, type)
          .then(res => {
            if (res.response.data.invalidFiles.length > 0) {
              this.$swal({
                icon: "error",
                title: "Erreur de téléchargement PDF !",
                text: this.displayInvalidFiles(res.response.data.invalidFiles),
                showConfirmButton: true,
                timer: 15000,
              });
            }
            if (res.response.data.validFiles.length > 0) {
              if (type == "invitation") {
                res.response.data.validFiles.forEach(file => {
                  this.buildingOwnersItems[
                    foundIndex
                  ].documents.individualAdditionalFileInvitation.push({
                    id: file.id,
                    path: links.API_WEB_APP_MEDIA_LINK(file.file_path),
                    label: file.name,
                  });
                });
              } else {
                res.response.data.validFiles.forEach(file => {
                  this.buildingOwnersItems[foundIndex].documents.individualAdditionalFilesPv.push({
                    id: file.id,
                    path: links.API_WEB_APP_MEDIA_LINK(file.file_path),
                    label: file.name,
                  });
                });
              }
            }
          })
          .catch(() => {
            this.$swal(this.alertError());
          });
      } else {
        this.$swal(this.alertError());
      }
    },
    deleteIndividualFile(individualFile, individualFileIndex, mandateId, type) {
      this.$swal(this.alertQuestion())
        .then(res => {
          if (res.value) {
            let foundIndex = this.$_.findIndex(this.buildingOwnersItems, item => {
              return item.mandateId == mandateId;
            });
            if (foundIndex != "-1") {
              if (type == "invitation") {
                this.buildingOwnersItems[
                  foundIndex
                ].documents.individualAdditionalFileInvitation.splice(individualFileIndex, 1);
              } else {
                this.buildingOwnersItems[foundIndex].documents.individualAdditionalFilesPv.splice(
                  individualFileIndex,
                  1
                );
              }
              this.DELETE_MEDIA(individualFile);
            } else {
              this.$swal(this.alertError());
            }
          }
        })
        .catch(err => {
          console.log("err", err);
          this.$swal(this.alertError());
        });
    },
    getQrCodeBase64Image(buildingOwnerId) {
      let result = null;
      QRCode.toDataURL(buildingOwnerId, function (err, url) {
        if (err) {
          console.error(err);
        } else {
          result = url;
        }
      });
      return result;
    },
    async fetchDocRequestsForMandate(mandateId) {
      const mandate = this.$_.find(this.mandates, item => {
        return item.id == mandateId;
      });
      if (mandate.doc_requests && mandate.doc_requests.length > 0) {
        this.rowDetailsIsLoading = false;
        this.rowDetailsIsLoadingForMandateId = null;
      } else {
        await MandateMW.GET_DOC_REQUESTS(mandateId).then(res => {
          this.rowDetailsIsLoading = false;
          this.rowDetailsIsLoadingForMandateId = null;

          let foundMandateIndex = this.$_.findIndex(this.mandates, item => {
            return item.id == mandateId;
          });
          this.mandates[foundMandateIndex]["doc_requests"] = res.response.data.doc_requests;
        });
      }
    },
    getLastDocStatusDetails(docsStatus) {
      let sortedArray = this.$_.sortBy(docsStatus, status => {
        if (status.DHStatus) {
          return status.DHStatus;
        } else if (status.createdAt) {
          return status.createdAt;
        } else {
          return null;
        }
      });

      sortedArray.reverse();

      if (sortedArray[0].Details) {
        return sortedArray[0].Details;
      } else if (sortedArray[0].recordStatus && sortedArray[0].recordRecipientStatus) {
        return "Status: " + lreRecordStatus[sortedArray[0].recordStatus];
      }
    },
    getLastDocStatusDateTime(docsStatus) {
      let sortedArray = this.$_.sortBy(docsStatus, status => {
        if (status.DHStatus) {
          return status.DHStatus;
        } else if (status.createdAt) {
          return status.createdAt;
        } else {
          return null;
        }
      });

      sortedArray.reverse();

      if (sortedArray[0].DHStatus) {
        return this.$moment(this.toUtc(sortedArray[0].DHStatus)).format("YYYY-MM-DD HH:mm:ss");
      } else if (sortedArray[0].createdAt) {
        return this.$moment(this.toUtc(sortedArray[0].createdAt)).format("YYYY-MM-DD HH:mm:ss");
      }
    },
    updateMeetingDocPrintColor() {
      let meeting = {
        id: this.meeting.id,
        docs_print_color: this.docsPrintColor.value,
        building_id: this.meeting.building_id,
        agencyId: this.agencyId,
      };

      MeetingMW.UPDATE_DOCS_PRINT_COLOR(meeting).catch(err => {
        this.$store.state.showLoaderOverlay = false;
      });
    },
    async showAskNotificationTypeModal() {
      const hasAgencyDetails = await this.hasAgencyDetails();
      if (!hasAgencyDetails) {
        return;
      }
      this.$bvModal.show("modal-ask-notification-type");
    },
    async hasAgencyDetails() {
      const agencyUser = this.getAgencyNameConfigs();
      const agencyEmail = this.getAgencyEmailConfigs();

      if (!agencyUser || !agencyEmail) {
        await this.$swal({
          icon: "warning",
          title: "Les détails de l'agence sont manquants !",
          html: "Allez dans Configurations/Agence Lre et ajoutez les détails de l'agence.",
          showConfirmButton: true,
          showCancelButton: true,
          confirmButtonText: "Ajouter les détails",
          confirmButtonClass: "btn btn-primary btn-lg",
          cancelButtonText: "Pas maintenant",
        })
          .then(res => {
            if (res.value) {
              this.$router.push({
                name: "configurations-agency-lre",
              });
            }
            return false;
          })
          .catch(() => {
            return false;
          });
      } else {
        return true;
      }
    },
    async search() {
      if (this.isTableLoading) return;

      this.hasSearchInputBeenReseted = false;

      await this.prepareBuildingOwnersData();
    },
    focusSearch() {
      this.isSearchInputFocused = true;
    },
    unfocusSearch() {
      this.isSearchInputFocused = false;
    },
    async resetSearch() {
      this.hasSearchInputBeenReseted = true;
      this.isSearchInputFocused = false;

      this.searchParams = "";
      await this.prepareBuildingOwnersData();
    },
    async setBoSortByName() {
      if (this.boSortByName == "ASC") {
        this.sortDesc = true;
        this.boSortByName = "DESC";
      } else {
        this.boSortByName = "ASC";
        this.sortDesc = false;
      }

      await this.prepareBuildingOwnersData();
    },

    async prepareBuildingOwnersData() {
      this.isTableLoading = true;

      await this.getMandates();

      this.buildingOwnersItems = [];
      this.processMandatesData();
      this.sortBuildingOwnersItems();

      this.isTableLoading = false;
    },

    showInvitationBroadcastField(item) {
      // NOTE: IF Recipient
      if (item.isRecipientMeetingDoc) {
        return this.showAgencyPreferredInvitationBroadcastField;
      } else {
        return this.showAgencyPreferredInvitationBroadcastField;
      }
    },
    async getBuildingsList() {
      BuildingORM.api()
        .BUILDINGS_LIST()
        .then(res => {
          this.buildingsList = [];

          let apiBuildings = res.response.data ? res.response.data["hydra:member"] : [];
          apiBuildings.forEach(item => {
            this.buildingsList.push({
              value: item.id,
              text: item.name,
            });
          });
        })
        .catch(err => {
          console.error(err);
        });
    },
    getBuildingAslName(id) {
      let found = this.$_.find(this.buildingsList, building => {
        return id == building.value;
      });
      if (found !== undefined) {
        return found.text;
      } else {
        return "";
      }
    },
  },
};
</script>
